import React, { useCallback, useState } from 'react';

import type {
  RadioProps,
  RadioGroupProps,
  RadioSize,
  RadioState,
} from './Radio.types';

/**
 * Radio component for single selection within a group.
 * Follows the FitPro design system with contrast-500 (Emerald) for the selected state.
 */
const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      description,
      error,
      success,
      size = 'md',
      className = '',
      disabled = false,
      id,
      labelPosition = 'right',
      variant = 'default',
      checked,
      ...props
    },
    ref
  ) => {
    const reactId = React.useId();
    const radioId = id || `radio-${reactId}`;

    // Determine state
    const state: RadioState = error ? 'error' : success ? 'success' : 'default';

    // Pill variant rendering
    // Touch targets: min-height 44px for mobile accessibility (WCAG 2.5.5)
    if (variant === 'pill') {
      const pillSizeClasses: Record<RadioSize, string> = {
        sm: 'min-h-9 px-3 py-2 text-label-xs',
        md: 'min-h-11 px-3 py-2 text-label-xs',
        lg: 'min-h-11 px-4 py-2.5 text-caption-sm',
      };

      const pillClasses = [
        'flex items-center justify-center rounded-full tracking-wider font-medium border transition-colors cursor-pointer',
        pillSizeClasses[size],
        checked
          ? 'text-primary-300 border-stroke-accent bg-sakura-wash'
          : 'text-neutral-400 border-stroke-default bg-contrast-700 hover:border-stroke-strong hover:text-neutral-200',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ');

      return (
        <label className={pillClasses}>
          <input
            ref={ref}
            id={radioId}
            type="radio"
            className="sr-only"
            disabled={disabled}
            checked={checked}
            aria-invalid={state === 'error'}
            {...props}
          />
          {label}
        </label>
      );
    }

    // Default variant rendering
    const sizeClasses: Record<RadioSize, string> = {
      sm: 'h-3.5 w-3.5',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
    };

    // Brand-styled state classes per design spec.
    //
    // `accent-*` is what actually paints a native radio's checked dot — `text-*`
    // does not reach it, so without this the control renders in the browser's
    // own blue, off-system on every screen it appears on.
    const stateClasses: Record<RadioState, string> = {
      default:
        'accent-primary-400 text-primary-400 border-stroke-default focus:border-primary-400',
      error:
        'accent-error-500 text-error-500 border-error-500 focus:border-error-500',
      success:
        'accent-success-500 text-success-500 border-success-500 focus:border-success-500',
    };

    const labelStateClasses: Record<RadioState, string> = {
      default: 'text-neutral-50',
      error: 'text-error-500',
      success: 'text-success-500',
    };

    const baseClasses =
      'focus-visible:ring-state-focus transition-colors duration-normal disabled:cursor-not-allowed disabled:opacity-50';

    const classes = [
      baseClasses,
      sizeClasses[size],
      stateClasses[state],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const labelSizeClasses: Record<RadioSize, string> = {
      sm: 'text-label-sm',
      md: 'text-label-sm',
      lg: 'text-body-md',
    };

    const radioElement = (
      <div className="flex items-center h-5">
        <input
          ref={ref}
          id={radioId}
          type="radio"
          className={classes}
          disabled={disabled}
          checked={checked}
          aria-invalid={state === 'error'}
          {...props}
        />
      </div>
    );

    const labelElement = (label || description) && (
      <div className={labelSizeClasses[size]}>
        {label && (
          <label
            htmlFor={radioId}
            className={`font-medium cursor-pointer ${labelStateClasses[state]} ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {label}
          </label>
        )}
        {description && (
          <p className="text-neutral-400 mt-0.5">{description}</p>
        )}
      </div>
    );

    return (
      <div
        className={`flex items-start gap-3 ${
          labelPosition === 'left' ? 'flex-row-reverse justify-end' : ''
        }`}
      >
        {radioElement}
        {labelElement}
      </div>
    );
  }
);

Radio.displayName = 'Radio';

/**
 * RadioGroup component for managing a group of radio buttons.
 */
const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  value: controlledValue,
  defaultValue = '',
  onChange,
  options,
  size = 'md',
  error,
  success,
  label: groupLabel,
  required = false,
  helpText,
  disabled = false,
  orientation = 'vertical',
  className = '',
  labelPosition = 'right',
  variant = 'default',
}) => {
  // Support both controlled and uncontrolled usage
  const [internalValue, setInternalValue] = useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      if (!isControlled) {
        setInternalValue(newValue);
      }

      onChange?.(newValue);
    },
    [isControlled, onChange]
  );

  const state: RadioState = error ? 'error' : success ? 'success' : 'default';

  const labelStateClasses: Record<RadioState, string> = {
    default: 'text-neutral-50',
    error: 'text-error-500',
    success: 'text-success-500',
  };

  return (
    <fieldset className={`space-y-2 ${className}`} disabled={disabled}>
      {groupLabel && (
        <legend
          className={`text-label-sm font-medium ${labelStateClasses[state]} ${
            disabled ? 'opacity-50' : ''
          }`}
        >
          {groupLabel}
          {required && <span className="ml-1 text-primary-400">*</span>}
        </legend>
      )}

      <div
        className={`flex ${
          variant === 'pill'
            ? 'flex-row flex-wrap gap-2'
            : orientation === 'horizontal'
              ? 'flex-row gap-6'
              : 'flex-col gap-3'
        }`}
        role="radiogroup"
        aria-label={groupLabel}
      >
        {options.map((option) => (
          <Radio
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            description={option.description!}
            checked={currentValue === option.value}
            onChange={handleChange}
            disabled={disabled || option.disabled}
            size={size}
            error={error!}
            success={!!success}
            labelPosition={labelPosition}
            variant={variant}
          />
        ))}
      </div>

      {error && <p className="text-body-sm text-error-500">{error}</p>}

      {helpText && !error && (
        <p className="text-label-xs text-neutral-400">{helpText}</p>
      )}
    </fieldset>
  );
};

RadioGroup.displayName = 'RadioGroup';

export { Radio, RadioGroup };
export default Radio;
